/**
 * Gestión → Clientes APP (`/gestion/clientes-app`).
 *
 * Puerto de `ClientesDiscount::index` + `views/clientes_discount/index.php`, que
 * en el panel anterior gestionaba dos cosas: los **clientes y vehículos de la
 * aplicación** (que viven en el servicio externo `api_base_url` y en
 * `customer_vehicles`) y los **descuentos** por producto (`customer_discounts`).
 *
 * Aquí se porta la parte local: los descuentos (`customer_discounts`) con su
 * cliente, su producto (`pts_fuel_grades`) y su placa, más los vehículos de la APP
 * (`customer_vehicles`), con su alta, su baja, la baja masiva de descuentos y la
 * corrección de la placa de un descuento. El alta y edición del cliente y del
 * vehículo **en el servicio externo** (`api_base_url`) sigue con candado.
 */

import {
  Globe,
  Info,
  Lock,
  Pencil,
  Percent,
  Plus,
  RefreshCw,
  Search,
  Smartphone,
  Trash2,
  Truck,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber, cn } from '@/lib/utils';

import {
  useActualizarDescuentoApp,
  useClientesApp,
  useClientesAppGenerales,
  useCrearClienteApp,
  useCrearDescuentoApp,
  useCrearVehiculoApp,
  useEditarClienteApp,
  useEliminarDescuentoApp,
  useEliminarDescuentosMasivo,
  useEliminarVehiculoApp,
  useVehiculosDeCliente,
} from '../cierre.api';
import {
  DialogoClienteApp,
  DialogoDescuentoApp,
  DialogoNuevoVehiculoApp,
  DialogoVehiculosDeCliente,
} from '../clientes-app-dialogos';
import { fechaCorta } from '../gestion-formato';
import type {
  ClienteAppGeneral,
  DatosClienteApp,
  DatosDescuentoApp,
  DescuentoApp,
  FiltrosClientesApp,
  VehiculoApp,
} from '../cierre-gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Etiqueta del tipo de descuento. */
function etiquetaTipo(tipo: string): string {
  if (tipo === 'document') return 'Por documento';
  if (tipo === 'license_plate') return 'Por placa';
  if (tipo === 'consumption') return 'Por consumo';

  return tipo;
}

export function ClientesAppPage() {
  useDocumentTitle('Gestión · Clientes APP');

  const [pestana, setPestana] = useState<'descuentos' | 'vehiculos' | 'clientes'>('clientes');
  const [busqueda, setBusqueda] = useState('');
  const [clientesBusqueda, setClientesBusqueda] = useState('');
  const [tipo, setTipo] = useState('');
  const [grado, setGrado] = useState('');
  const [soloVigentes, setSoloVigentes] = useState(true);
  const [estado, setEstado] = useState('1');

  const filtros = useMemo<FiltrosClientesApp>(
    () => ({
      search: busqueda.trim(),
      discount_type: tipo,
      fuel_grade_id: grado ? Number(grado) : undefined,
      vigentes: soloVigentes ? '1' : '',
      estado: pestana === 'vehiculos' ? estado : undefined,
    }),
    [busqueda, tipo, grado, soloVigentes, estado, pestana],
  );

  const consulta = useClientesApp(filtros);
  const datos = consulta.data;

  const [aviso, setAviso] = useState<string | null>(null);
  const [nuevoVehiculo, setNuevoVehiculo] = useState(false);
  const [clienteNuevo, setClienteNuevo] = useState(false);
  const [clienteEnEdicion, setClienteEnEdicion] = useState<ClienteAppGeneral | null>(null);
  const [descuentoEnEdicion, setDescuentoEnEdicion] = useState<DescuentoApp | null>(null);
  const [descuentoNuevo, setDescuentoNuevo] = useState(false);
  const [vehiculoDeCliente, setVehiculoDeCliente] = useState<VehiculoApp | null>(null);
  const [porEliminar, setPorEliminar] = useState<
    { tipo: 'descuento' | 'vehiculo'; id: number; etiqueta: string } | null
  >(null);
  const [seleccion, setSeleccion] = useState<number[]>([]);
  const [bajaMasiva, setBajaMasiva] = useState(false);

  const crearVehiculo = useCrearVehiculoApp();
  const eliminarVehiculo = useEliminarVehiculoApp();
  const crearDescuento = useCrearDescuentoApp();
  const actualizarDescuento = useActualizarDescuentoApp();
  const eliminarDescuento = useEliminarDescuentoApp();
  const eliminarMasivo = useEliminarDescuentosMasivo();
  const clientesGenerales = useClientesAppGenerales(true);
  const crearClienteApp = useCrearClienteApp();
  const editarClienteApp = useEditarClienteApp();

  const vehiculosDelCliente = useVehiculosDeCliente(vehiculoDeCliente?.customer_id ?? null);

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  /** Traduce el fallo de la API a un mensaje para el usuario. */
  const fallo = (error: unknown, porDefecto: string) =>
    error instanceof ApiError ? error.message : porDefecto;

  const descuentos = datos?.descuentos ?? [];
  const vehiculos = datos?.vehiculos ?? [];
  const todosSeleccionados = descuentos.length > 0 && seleccion.length === descuentos.length;

  // Clientes de la APP (servicio externo) con su búsqueda en el navegador.
  const clientesLista = clientesGenerales.data?.clientes ?? [];
  const clientesVisibles = clientesBusqueda.trim()
    ? clientesLista.filter((cliente) => {
        const termino = clientesBusqueda.trim().toLowerCase();

        return [cliente.nombre, cliente.numero_doc, cliente.direccion, cliente.telefono]
          .filter(Boolean)
          .some((valor) => String(valor).toLowerCase().includes(termino));
      })
    : clientesLista;

  // Cada pestaña lleva su propio paginado.
  const paginadoDescuentos = usePaginacion(descuentos, { clave: [filtros, 'descuentos'] });
  const paginadoVehiculos = usePaginacion(vehiculos, { clave: [filtros, 'vehiculos'] });
  const paginadoClientes = usePaginacion(clientesVisibles, { clave: ['clientes', clientesBusqueda] });

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Clientes APP</h1>
          <p className="mt-1 text-sm text-ink-500">
            Clientes, descuentos por producto y vehículos registrados en la aplicación, con su
            acceso al portal del cliente.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          leftIcon={
            <RefreshCw
              className={cn('size-4', consulta.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
          }
          onClick={() => void consulta.refetch()}
        >
          Actualizar
        </Button>
      </header>

      {datos ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[
            { etiqueta: 'Clientes', valor: String(clientesGenerales.data?.total ?? 0), detalle: 'Registrados en la APP' },
            { etiqueta: 'Descuentos', valor: String(datos.resumen.total), detalle: `${datos.resumen.activos} activos · ${datos.resumen.vencidos} vencidos` },
            { etiqueta: 'Por documento', valor: String(datos.resumen.por_documento), detalle: 'Descuento por cliente' },
            { etiqueta: 'Por placa', valor: String(datos.resumen.por_placa), detalle: 'Descuento por vehículo' },
            { etiqueta: 'Con descuento', valor: String(datos.resumen.clientes), detalle: 'Clientes con descuento' },
            { etiqueta: 'Vehículos', valor: String(datos.resumen.vehiculos), detalle: `${datos.resumen.vehiculos_activos} activos` },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <nav className="flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0" aria-label="Secciones de clientes APP">
            {[
              { clave: 'clientes' as const, label: 'Clientes', icono: Users },
              { clave: 'descuentos' as const, label: 'Descuentos', icono: Percent },
              { clave: 'vehiculos' as const, label: 'Vehículos', icono: Smartphone },
            ].map((opcion) => (
              <button
                key={opcion.clave}
                aria-current={pestana === opcion.clave ? 'page' : undefined}
                className={cn(
                  'inline-flex shrink-0 items-center gap-2 rounded-control px-3 py-2 text-xs font-semibold transition-colors',
                  pestana === opcion.clave
                    ? 'bg-ink-900 text-white'
                    : 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700',
                )}
                onClick={() => setPestana(opcion.clave)}
              >
                <opcion.icono className="size-3.5" aria-hidden="true" />
                {opcion.label}
              </button>
            ))}

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-xs text-ink-500">
                {pestana === 'descuentos'
                  ? `${datos?.descuentos.length ?? 0} descuentos`
                  : pestana === 'vehiculos'
                    ? `${datos?.vehiculos.length ?? 0} vehículos`
                    : `${clientesGenerales.data?.total ?? 0} clientes`}
              </span>

              {pestana === 'descuentos' ? (
                <>
                  {seleccion.length > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Trash2 className="size-4" aria-hidden="true" />}
                      onClick={() => setBajaMasiva(true)}
                    >
                      Dar de baja ({seleccion.length})
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    leftIcon={<Plus className="size-4" aria-hidden="true" />}
                    onClick={() => setDescuentoNuevo(true)}
                  >
                    Nuevo descuento
                  </Button>
                </>
              ) : pestana === 'vehiculos' ? (
                <Button
                  size="sm"
                  leftIcon={<Plus className="size-4" aria-hidden="true" />}
                  onClick={() => setNuevoVehiculo(true)}
                >
                  Nuevo vehículo
                </Button>
              ) : (
                <Button
                  size="sm"
                  leftIcon={<Plus className="size-4" aria-hidden="true" />}
                  onClick={() => setClienteNuevo(true)}
                >
                  Nuevo cliente
                </Button>
              )}
            </div>
          </nav>

          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-500 sm:col-span-2">
              Buscar
              <span className="relative block">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
              <input
                className={cn(CONTROL, 'pl-9')}
                placeholder={
                  pestana === 'clientes'
                    ? 'Nombre, documento o dirección…'
                    : 'Cliente, documento, placa o producto…'
                }
                value={pestana === 'clientes' ? clientesBusqueda : busqueda}
                onChange={(evento) =>
                  pestana === 'clientes'
                    ? setClientesBusqueda(evento.target.value)
                    : setBusqueda(evento.target.value)
                }
              />
                        </span>
            </label>

            {pestana === 'descuentos' ? (
              <>
                <label className="flex flex-col gap-1 text-xs text-ink-500">
                  Tipo de descuento
                  <span className="relative block">
                  <Select
                    className={CONTROL}
                    value={tipo}
                    onChange={(evento) => setTipo(evento.target.value)}
                  >
                    <option value="">Todos los tipos</option>
                    <option value="document">Por documento</option>
                    <option value="license_plate">Por placa</option>
                    <option value="consumption">Por consumo</option>
                  </Select>
                                </span>
                </label>

                <label className="flex flex-col gap-1 text-xs text-ink-500">
                  Producto
                  <span className="relative block">
                  <Select
                    className={CONTROL}
                    value={grado}
                    onChange={(evento) => setGrado(evento.target.value)}
                  >
                    <option value="">Todos los productos</option>
                    {(datos?.grados ?? []).map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.nombre}
                      </option>
                    ))}
                  </Select>
                                </span>
                </label>
              </>
            ) : pestana === 'vehiculos' ? (
              <label className="flex flex-col gap-1 text-xs text-ink-500">
                Estado del vehículo
                <span className="relative block">
                <Select
                  className={CONTROL}
                  value={estado}
                  onChange={(evento) => setEstado(evento.target.value)}
                >
                  <option value="1">Activos</option>
                  <option value="todos">Todos</option>
                </Select>
                            </span>
              </label>
            ) : null}
          </div>

          {pestana === 'descuentos' ? (
            <label className="flex items-center gap-2 text-xs text-ink-600">
              <input
                type="checkbox"
                className="size-4 rounded border-ink-300"
                checked={soloVigentes}
                onChange={(evento) => setSoloVigentes(evento.target.checked)}
              />
              Sólo descuentos vigentes
            </label>
          ) : null}

          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron cargar los datos de la APP">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          {aviso ? (
            <Alert variant="success" title="Operación realizada">
              {aviso}
            </Alert>
          ) : null}

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading && pestana === 'descuentos' ? (
            <div className="max-h-[58vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-9 px-2 py-2">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todos los descuentos"
                        className="size-4 rounded border-ink-300"
                        checked={todosSeleccionados}
                        disabled={descuentos.length === 0}
                        onChange={(evento) =>
                          setSeleccion(
                            evento.target.checked ? descuentos.map((item) => item.id) : [],
                          )
                        }
                      />
                    </th>
                    <th className="px-2 py-2">Cliente</th>
                    <th className="px-2 py-2">Producto / tipo</th>
                    <th className="px-2 py-2 text-right">Descuento / vigencia</th>
                    <th className="px-2 py-2">Placa</th>
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {descuentos.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={7} className="px-3 py-10 text-center text-sm text-ink-500">
                        No hay descuentos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginadoDescuentos.visibles.map((descuento) => (
                    <tr key={descuento.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-2 py-2 align-top">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar el descuento #${descuento.id}`}
                          className="size-4 rounded border-ink-300"
                          checked={seleccion.includes(descuento.id)}
                          onChange={(evento) =>
                            setSeleccion((actual) =>
                              evento.target.checked
                                ? [...actual, descuento.id]
                                : actual.filter((item) => item !== descuento.id),
                            )
                          }
                        />
                      </td>
                      <td className="max-w-[13rem] px-2 py-2">
                        <p className="truncate font-semibold text-ink-900" title={descuento.cliente ?? ''}>
                          {descuento.cliente ?? `Cliente APP #${descuento.customer_id}`}
                        </p>
                        <p className="truncate text-xs text-ink-500">{descuento.cliente_doc ?? ''}</p>
                      </td>
                      <td className="px-2 py-2">
                        <p className="font-medium text-ink-800">{descuento.combustible ?? '—'}</p>
                        <p className="text-xs text-ink-500">
                          {etiquetaTipo(descuento.discount_type)}
                          {descuento.discount_type === 'consumption' &&
                          descuento.min_consumption !== null
                            ? ` · ${formatNumber(descuento.min_consumption)}–${formatNumber(
                                descuento.max_consumption ?? 0,
                              )}`
                            : ''}
                        </p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <p className="font-semibold">
                          {descuento.discount_amount === null
                            ? '—'
                            : descuento.discount_amount.toLocaleString('es-PE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                        </p>
                        {descuento.discount_amount !== null && descuento.discount_amount !== 0 ? (
                          <p
                            className={
                              descuento.discount_amount > 0
                                ? 'text-xs font-medium text-emerald-600'
                                : 'text-xs font-medium text-amber-600'
                            }
                          >
                            {descuento.discount_amount > 0 ? 'Descuento' : 'Recargo'}
                          </p>
                        ) : null}
                        <p className="text-xs text-ink-500">
                          {descuento.valid_until
                            ? descuento.vencido
                              ? `venció ${fechaCorta(descuento.valid_until)}`
                              : `hasta ${fechaCorta(descuento.valid_until)}`
                            : 'sin vencimiento'}
                        </p>
                        {descuento.valid_until && !descuento.vencido ? (
                          <p className="text-xs text-ink-400">
                            {descuento.dias_restantes ?? 0} días
                          </p>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-ink-600">
                        {descuento.placa ?? '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <Badge
                          variant={
                            !descuento.status ? 'neutral' : descuento.vencido ? 'danger' : 'success'
                          }
                        >
                          {!descuento.status ? 'INACTIVO' : descuento.vencido ? 'VENCIDO' : 'ACTIVO'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title={`Editar el descuento #${descuento.id}`}
                            aria-label={`Editar el descuento #${descuento.id}`}
                            disabled={!descuento.status}
                            onClick={() => setDescuentoEnEdicion(descuento)}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={`Dar de baja el descuento #${descuento.id}`}
                            aria-label={`Dar de baja el descuento #${descuento.id}`}
                            disabled={!descuento.status}
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() =>
                              setPorEliminar({
                                tipo: 'descuento',
                                id: descuento.id,
                                etiqueta: `${descuento.cliente ?? `Cliente #${descuento.customer_id}`} · ${
                                  descuento.combustible ?? 'producto'
                                }`,
                              })
                            }
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!consulta.isLoading && pestana === 'descuentos' ? (
            <Paginador {...paginadoDescuentos} etiqueta="descuentos" />
          ) : null}

          {!consulta.isLoading && pestana === 'vehiculos' ? (
            <div className="max-h-[58vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Placa</th>
                    <th className="px-2 py-2">Cliente</th>
                    <th className="px-2 py-2">Tipo / vehículo</th>
                    <th className="px-2 py-2 text-right">Descuentos</th>
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.vehiculos ?? []).length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={6} className="px-3 py-10 text-center text-sm text-ink-500">
                        No hay vehículos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginadoVehiculos.visibles.map((vehiculo) => (
                    <tr key={vehiculo.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-2 py-2 font-semibold whitespace-nowrap text-ink-900">
                        {vehiculo.placa}
                      </td>
                      <td className="max-w-[13rem] px-2 py-2">
                        <p className="truncate text-ink-800" title={vehiculo.cliente ?? ''}>
                          {vehiculo.cliente ?? '—'}
                        </p>
                        <p className="truncate text-xs text-ink-500">{vehiculo.cliente_doc ?? ''}</p>
                      </td>
                      <td className="px-2 py-2 text-xs text-ink-600">
                        <p>{vehiculo.tipo ?? '—'}</p>
                        <p className="text-ink-500">
                          {[vehiculo.marca, vehiculo.modelo, vehiculo.anio]
                            .filter(Boolean)
                            .join(' ') || 'sin detalle'}
                        </p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {vehiculo.descuentos > 0 ? (
                          <Badge variant="brand">{vehiculo.descuentos}</Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <Badge variant={vehiculo.status ? 'success' : 'neutral'}>
                          {vehiculo.status ? 'ACTIVO' : 'INACTIVO'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title={`Vehículos de ${vehiculo.cliente ?? 'este cliente'}`}
                            aria-label={`Ver los vehículos de ${vehiculo.cliente ?? 'este cliente'}`}
                            onClick={() => setVehiculoDeCliente(vehiculo)}
                          >
                            <Truck className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={`Dar de baja la placa ${vehiculo.placa}`}
                            aria-label={`Dar de baja la placa ${vehiculo.placa}`}
                            disabled={!vehiculo.status}
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() =>
                              setPorEliminar({
                                tipo: 'vehiculo',
                                id: vehiculo.id,
                                etiqueta: vehiculo.placa,
                              })
                            }
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!consulta.isLoading && pestana === 'vehiculos' ? (
            <Paginador {...paginadoVehiculos} etiqueta="vehículos" />
          ) : null}

          {pestana === 'clientes' ? (
            <>
              {clientesGenerales.isLoading ? <Skeleton className="h-64 w-full" /> : null}

              {clientesGenerales.isError ? (
                <Alert variant="error" title="No se pudieron cargar los clientes de la APP">
                  {clientesGenerales.error instanceof ApiError
                    ? clientesGenerales.error.message
                    : 'Inténtalo de nuevo en unos segundos.'}
                </Alert>
              ) : null}

              {!clientesGenerales.isLoading && !clientesGenerales.isError ? (
                <>
                  <div className="max-h-[58vh] overflow-auto rounded-card border border-ink-100">
                    <table className="w-full border-collapse text-[13px]">
                      <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                        <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                          <th className="px-2 py-2">Cliente</th>
                          <th className="px-2 py-2">Documento</th>
                          <th className="px-2 py-2">Dirección</th>
                          <th className="px-2 py-2">Estado</th>
                          <th className="px-2 py-2 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginadoClientes.visibles.length === 0 ? (
                          <tr className="border-t border-ink-100">
                            <td colSpan={5} className="px-3 py-10 text-center text-sm text-ink-500">
                              No hay clientes que cumplan la búsqueda.
                            </td>
                          </tr>
                        ) : null}

                        {paginadoClientes.visibles.map((cliente) => (
                          <tr key={cliente.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                            <td className="max-w-[16rem] px-2 py-2">
                              <div className="flex items-center gap-2">
                                <p
                                  className="truncate font-semibold text-ink-900"
                                  title={cliente.nombre ?? ''}
                                >
                                  {cliente.nombre_comercial || cliente.nombre || `Cliente #${cliente.id}`}
                                </p>
                                {cliente.portal_activo ? (
                                  <Badge
                                    variant="brand"
                                    title={
                                      cliente.portal_ultimo_acceso
                                        ? `Portal del cliente activo · último acceso ${fechaCorta(cliente.portal_ultimo_acceso)}`
                                        : 'Portal del cliente activo · sin accesos todavía'
                                    }
                                  >
                                    <Globe className="mr-1 inline size-3" aria-hidden="true" />
                                    Portal
                                  </Badge>
                                ) : null}
                              </div>
                              {cliente.nombre_comercial && cliente.nombre ? (
                                <p className="truncate text-xs text-ink-500" title={cliente.nombre}>
                                  {cliente.nombre}
                                </p>
                              ) : null}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap text-ink-700">
                              {cliente.tipo_doc === '6' ? 'RUC' : cliente.tipo_doc === '1' ? 'DNI' : cliente.tipo_doc}{' '}
                              {cliente.numero_doc ?? '—'}
                              {cliente.withholding ? (
                                <span className="mt-1 block">
                                  <Badge
                                    variant="warning"
                                    title={`Aplica retención del ${cliente.withholding_percentage ?? 0}%`}
                                  >
                                    Retención{cliente.withholding_percentage ? ` ${cliente.withholding_percentage}%` : ''}
                                  </Badge>
                                </span>
                              ) : null}
                            </td>
                            <td className="max-w-[14rem] truncate px-2 py-2 text-xs text-ink-600" title={cliente.direccion ?? ''}>
                              {cliente.direccion ?? '—'}
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              <Badge variant={cliente.estado ? 'success' : 'neutral'}>
                                {cliente.estado ? 'ACTIVO' : 'INACTIVO'}
                              </Badge>
                            </td>
                            <td className="px-2 py-2 text-right whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="icon"
                                title={`Editar ${cliente.nombre_comercial || cliente.nombre || 'el cliente'}`}
                                aria-label={`Editar ${cliente.nombre_comercial || cliente.nombre || 'el cliente'}`}
                                onClick={() => setClienteEnEdicion(cliente)}
                              >
                                <Pencil className="size-4" aria-hidden="true" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Paginador {...paginadoClientes} etiqueta="clientes" />
                </>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <Alert variant="info" title="Pendiente de migrar en Clientes APP">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              La edición del vehículo en el servicio externo que atiende la aplicación (aquí se
              administra el registro local de <code>customer_vehicles</code>).
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Los descuentos se guardan en la tabla de descuentos de la APP con su cliente local, su
        producto del catálogo de combustibles y, si aplica, su placa. Dar de baja una placa
        desactiva también los descuentos que apuntaban a ella.
      </p>

      <DialogoNuevoVehiculoApp
        open={nuevoVehiculo}
        onClose={() => setNuevoVehiculo(false)}
        guardando={crearVehiculo.isPending}
        error={crearVehiculo.error}
        onGuardar={(clienteId, datosVehiculo) =>
          crearVehiculo.mutate(
            { clienteId, datos: datosVehiculo },
            {
              onSuccess: (respuesta) => {
                setNuevoVehiculo(false);
                mostrar(`Vehículo #${respuesta.id} registrado en la APP.`);
              },
            },
          )
        }
      />

      <DialogoClienteApp
        open={clienteNuevo || clienteEnEdicion !== null}
        cliente={clienteEnEdicion}
        guardando={crearClienteApp.isPending || editarClienteApp.isPending}
        error={clienteEnEdicion ? editarClienteApp.error : crearClienteApp.error}
        onCerrar={() => {
          setClienteNuevo(false);
          setClienteEnEdicion(null);
        }}
        onGuardar={(datos: DatosClienteApp) => {
          if (clienteEnEdicion) {
            editarClienteApp.mutate(
              { id: clienteEnEdicion.id, datos },
              {
                onSuccess: (respuesta) => {
                  setClienteEnEdicion(null);
                  mostrar(respuesta.mensaje);
                },
              },
            );

            return;
          }

          crearClienteApp.mutate(datos, {
            onSuccess: (respuesta) => {
              setClienteNuevo(false);
              mostrar(respuesta.mensaje);
            },
          });
        }}
      />

      {descuentoNuevo || descuentoEnEdicion ? (
        <DialogoDescuentoApp
          key={descuentoEnEdicion?.id ?? 'nuevo'}
          open
          descuento={descuentoEnEdicion}
          onClose={() => {
            setDescuentoNuevo(false);
            setDescuentoEnEdicion(null);
          }}
          guardando={crearDescuento.isPending || actualizarDescuento.isPending}
          error={descuentoEnEdicion ? actualizarDescuento.error : crearDescuento.error}
          onGuardar={(datos: DatosDescuentoApp) => {
            const enEdicion = descuentoEnEdicion;

            if (enEdicion) {
              actualizarDescuento.mutate(
                { id: enEdicion.id, datos },
                {
                  onSuccess: (respuesta) => {
                    setDescuentoEnEdicion(null);
                    mostrar(respuesta.mensaje);
                  },
                },
              );

              return;
            }

            crearDescuento.mutate(datos, {
              onSuccess: (respuesta) => {
                setDescuentoNuevo(false);
                mostrar(respuesta.mensaje);
              },
            });
          }}
        />
      ) : null}

      <DialogoVehiculosDeCliente
        open={vehiculoDeCliente !== null}
        vehiculo={vehiculoDeCliente}
        vehiculos={vehiculosDelCliente.data?.vehiculos}
        cargando={vehiculosDelCliente.isLoading}
        error={vehiculosDelCliente.error}
        onCerrar={() => setVehiculoDeCliente(null)}
        onNuevo={() => {
          setVehiculoDeCliente(null);
          setNuevoVehiculo(true);
        }}
        onEliminar={(vehiculoId) =>
          setPorEliminar({
            tipo: 'vehiculo',
            id: vehiculoId,
            etiqueta: vehiculosDelCliente.data?.vehiculos.find((item) => item.id === vehiculoId)
              ?.license_plate ?? `Vehículo #${vehiculoId}`,
          })
        }
      />

      <ConfirmDialog
        open={bajaMasiva}
        title={`¿Dar de baja ${seleccion.length} descuento(s)?`}
        description="Los descuentos dados de baja dejan de aplicarse en la aplicación. Se conservan en el histórico y se pueden volver a crear."
        confirmLabel="Dar de baja"
        tone="danger"
        loading={eliminarMasivo.isPending}
        onClose={() => setBajaMasiva(false)}
        onConfirm={() => {
          eliminarMasivo.mutate(seleccion, {
            onSuccess: (respuesta) => {
              setBajaMasiva(false);
              setSeleccion([]);
              mostrar(respuesta.mensaje);
            },
            onError: (error) => {
              setBajaMasiva(false);
              mostrar(fallo(error, 'No se pudieron dar de baja los descuentos.'));
            },
          });
        }}
      />

      <ConfirmDialog
        open={porEliminar !== null}
        title={
          porEliminar?.tipo === 'vehiculo'
            ? `¿Dar de baja el vehículo ${porEliminar.etiqueta}?`
            : `¿Dar de baja el descuento #${porEliminar?.id ?? ''}?`
        }
        description={
          porEliminar?.tipo === 'vehiculo'
            ? 'El vehículo deja de estar disponible en la APP y sus descuentos activos también se dan de baja.'
            : `Se dará de baja el descuento de ${porEliminar?.etiqueta ?? ''}. Podrás volver a crearlo cuando lo necesites.`
        }
        confirmLabel="Dar de baja"
        tone="danger"
        loading={eliminarDescuento.isPending || eliminarVehiculo.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={() => {
          const objetivo = porEliminar;

          setPorEliminar(null);

          if (!objetivo) return;

          if (objetivo.tipo === 'vehiculo') {
            eliminarVehiculo.mutate(objetivo.id, {
              onSuccess: (respuesta) => {
                setVehiculoDeCliente(null);
                mostrar(
                  respuesta.descuentos_desactivados
                    ? `${respuesta.mensaje} Se dieron de baja ${respuesta.descuentos_desactivados} descuento(s).`
                    : respuesta.mensaje,
                );
              },
              onError: (error) => mostrar(fallo(error, 'No se pudo dar de baja el vehículo.')),
            });

            return;
          }

          eliminarDescuento.mutate(objetivo.id, {
            onSuccess: (respuesta) => mostrar(respuesta.mensaje),
            onError: (error) => mostrar(fallo(error, 'No se pudo dar de baja el descuento.')),
          });
        }}
      />
    </div>
  );
}
